// eslint-disable-next-line import/no-named-as-default
import Icon from "@mdi/react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";

import { mdiContentCopy } from "@mdi/js";
import { Popover, Radio, RadioChangeEvent } from "antd";
import { DEFAULT_MIDSTREAM_SETTINGS } from "constants/app.constants";
import { RootState } from "store/rootReducer";

import useFeatureFlags from "hooks/useFeatureFlags";

import { EntityKind } from "models/entityKind";

// import PopOver from "components/base/Popover";
import Indicator from "components/ui/Indicator";
import { useUserSettings } from "components/user/hooks";
import { updateEntityKind, useWellList } from "components/well-list/context";
import { useWellsFromFilter } from "components/well-list/hooks";

import {
  CopyButton,
  CopyContainer,
  EntityWrapper,
  InnerContainer,
  RootContainer,
  StyledRadioGroup,
  TopRow
} from "./MidstreamNavigator.styles";
import EntitySelector from "./buttons/EntitySelector";
import MinContributionPercentInput from "./buttons/MinContributionPercentInput";
import MonthSelectorButton from "./buttons/MonthSelectorButton";
import {
  midstreamNavigatorActions,
  useMidstreamNavigatorContext
} from "./context/MidstreamNavigatorContext";
import MidstreamNavigatorTable, {
  MidstreamNavigatorTableRef
} from "./table/MidstreamNavigatorTable";

const MidstreamNavigator: React.FC = () => {
  const [midstreamState, midstreamDispatch] = useMidstreamNavigatorContext();
  const [wellListState, wellListDispatch] = useWellList();
  const [copied, setCopied] = useState(false);
  const tableRef = useRef<MidstreamNavigatorTableRef>(null);
  const { hasFeature } = useFeatureFlags();
  const { isLoading: isWellListLoading } = useWellsFromFilter();

  const activeEntities = useSelector((state: RootState) => state.app.activeEntityKinds);
  const userSettings = useUserSettings();
  const isMidstreamDataSourceEnabled =
    userSettings?.midstreamSettings?.enabled ?? DEFAULT_MIDSTREAM_SETTINGS;

  const hiddenMessage = useMemo(() => {
    if (!activeEntities.includes(EntityKind.Facility)) {
      return isMidstreamDataSourceEnabled
        ? "Midstream is hidden"
        : "Midstream is disabled";
    }
    return null;
  }, [activeEntities, isMidstreamDataSourceEnabled]);

  const showOverlay = useMemo(() => {
    return !activeEntities.includes(EntityKind.Facility);
  }, [activeEntities]);

  // Keep WellListProvider synchronized with MidstreamNavigator mode
  useEffect(() => {
    if (wellListState.entityKind !== midstreamState.mode) {
      updateEntityKind(wellListDispatch, midstreamState.mode);
    }
  }, [midstreamState.mode, wellListState.entityKind, wellListDispatch]);

  const handleModeChange = (e: RadioChangeEvent) => {
    const newMode = e.target.value;
    midstreamDispatch(midstreamNavigatorActions.setMode(newMode));
  };

  const handleCopyTable = async () => {
    if (tableRef.current) {
      await tableRef.current.copyTableToClipboard();
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <RootContainer>
      {showOverlay ? (
        <Indicator map={false} message={hiddenMessage} />
      ) : (
        <>
          <TopRow>
            <StyledRadioGroup
              value={midstreamState.mode}
              onChange={handleModeChange}
              optionType="button"
              buttonStyle="solid"
              data-testid="mode-selector">
              <Radio.Button
                value={EntityKind.Facility}
                data-testid="facility-mode-button">
                Facility
              </Radio.Button>
              <Radio.Button value={EntityKind.Well} data-testid="well-mode-button">
                Well
              </Radio.Button>
            </StyledRadioGroup>

            <EntityWrapper>
              <EntitySelector />
            </EntityWrapper>

            <InnerContainer>
              {midstreamState.mode === EntityKind.Well && (
                <>
                  <MonthSelectorButton />
                  <MinContributionPercentInput />
                </>
              )}

              <CopyContainer>
                {hasFeature("Midstream Export") && (
                  <Popover
                    open={copied}
                    trigger="click"
                    content={<div>Copied to clipboard</div>}>
                    <CopyButton
                      onClick={handleCopyTable}
                      icon={<Icon path={mdiContentCopy} size={1.0} />}
                      placement="bottomRight">
                      Copy Table
                    </CopyButton>
                  </Popover>
                )}
              </CopyContainer>
            </InnerContainer>
          </TopRow>
          <MidstreamNavigatorTable
            ref={tableRef}
            showOverlay={showOverlay}
            isWellListLoading={isWellListLoading}
          />
        </>
      )}
    </RootContainer>
  );
};

export default MidstreamNavigator;
